import React, { useState } from "react";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Link2,
  SlidersHorizontal,
  Loader,
} from "lucide-react";
import { withdrawalStatuses } from "../../constants/withdrawalStatuses";
import { useWithdrawals } from "../../hooks/useWithdrawals";
import { WithdrawalFilter } from "../../components/WithdrawalFilter";
import { ExportButton } from "../../components/ExportButton";
import { WithdrawalModal } from "../../components/WithdrawalModal";
import { SuccessModal } from "../../components/SuccessModal";

const getStatusVisual = (status: string) => {
  const found = withdrawalStatuses.find((s) => s.value === status);
  return {
    label: found?.label || status,
    className: found?.color || "bg-gray-400 text-white",
  };
};

export const WithdrawalsPage: React.FC = () => {
  const {
    items,
    page,
    totalPages,
    loading,
    errorMessage,
    setPage,
    filters,
    applyFilters,
  } = useWithdrawals();

  const [filterOpen, setFilterOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const activeFilterCount = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (key === "statuses" && Array.isArray(value))
        return acc + (value.length > 0 ? 1 : 0);
      if (value !== undefined && value !== "") return acc + 1;
      return acc;
    },
    0
  );

  return (
    <div className="min-h-screen px-4 pt-6 text-sm text-[#1f1f1f] dark:text-white bg-gray-100 dark:bg-[#121212]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div className="flex items-center gap-6">
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Withdrawals
          </h1>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Plus size={16} /> Create
          </button>
        </div>

        <div className="flex items-center gap-2">
          <ExportButton
            filters={filters}
            endpoint="/withdrawals/external/export"
            // endpoint="back/withdrawals/external/export"
            filenamePrefix="withdrawals"
          />

          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            <SlidersHorizontal size={16} />
            Filter
            <span className="ml-1 text-xs text-white bg-green-600 rounded-full px-2 py-0.5">
              {activeFilterCount}
            </span>
          </button>
        </div>
      </div>

      {modalOpen && (
        <WithdrawalModal
          onClose={() => setModalOpen(false)}
          onSuccess={() => setSuccessOpen(true)}
        />
      )}

      {successOpen && <SuccessModal onClose={() => setSuccessOpen(false)} />}

      {filterOpen && (
        <WithdrawalFilter
          onClose={() => setFilterOpen(false)}
          filters={filters}
          setFilters={applyFilters}
        />
      )}

      {errorMessage && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 border border-red-300">
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <Loader className="animate-spin mx-auto mb-3" />
          Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          Nothing found for the specified filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-700">
          <table className="min-w-full bg-white dark:bg-[#1a1a1a]">
            <thead>
              <tr className="bg-[#f5f5f5] dark:bg-[#2a2a2a] text-xs font-semibold uppercase">
                <th className="px-4 py-3 text-center">ID</th>
                <th className="px-4 py-3 text-center">Date</th>
                <th className="px-4 py-3 text-center">Wallet</th>
                <th className="px-4 py-3 text-center">Txid</th>
                <th className="px-4 py-3 text-center">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const date = new Date(item.withdrawalTime);
                const status = getStatusVisual(item.status);

                return (
                  <tr
                    key={item.id}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-2 justify-center">
                        <span className="bg-gray-600 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
                          ID
                        </span>
                        <span className="font-medium">{item.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div>{format(date, "dd.MM.yyyy")}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {format(date, "HH:mm")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.wallet ? item.wallet : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.transactionHash ? (
                        <a
                          href={`https://tronscan.org/#/transaction/${item.transactionHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 text-blue-500 hover:underline"
                        >
                          <Link2 size={14} />
                          {item.transactionHash.slice(0, 5)}...
                          {item.transactionHash.slice(-4)}
                        </a>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.amountWithdrawal.toFixed(3)} $
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium bg-green-600 ${status.className}`}
                      >
                        {status.label}Paid
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-1">
          <button
            onClick={() => page > 0 && setPage(page - 1)}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            disabled={page === 0}
          >
            <ChevronLeft />
          </button>
          <span className="mx-2 text-sm">
            Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            onClick={() => page < totalPages - 1 && setPage(page + 1)}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            disabled={page >= totalPages - 1}
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};
